/**react*/
import * as React from "react";
import { useState, useEffect } from "react";
import "rsuite/dist/rsuite.css";
import { Helmet } from "react-helmet-async";
import SearchIcon from "@mui/icons-material/Search";
import {
    Box,
    InputAdornment,
    Stack,
    TextField,
    Typography,
    useTheme,    
    Button,
    Divider
} from "@mui/material";

import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";


/**mui*/
import {
    DataGridPro,
    GridColDef,
    GridRowsProp,
    GridRowModel,
    GridRowId    
} from "@mui/x-data-grid-pro";
/**local*/
import {
    useGetPerseusPaySplitConfigurationsQuery,
    useLazyGetSplitConfigDetailsQuery,
    useDeleteSplitConfigProfileMutation,
} from "../../../services/API/perseusPayAPISlice";
import ConfirmDialog from "../../../common/components/ConfirmDialog";
import PaymentMehtodType from "../../../common/components/paymentmethod.type";
import { isEmpty } from "../../../utils/Util";

const PricingModelList = () => {
    const theme = useTheme();
        // Dialog Setup
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogTitle, setDialogTitle] = useState("");
    const [dialogContent, setDialogContent] = useState("");
    const [onConfirm, setOnConfirm] = useState<() => void>(() => () => { });
    // Master & detail rows
    const [masterRows, setMasterRows] = useState<GridRowsProp>([]);
    const [detailData, setDetailData] = useState<Record<number, GridRowsProp>>({});
    const [expandedRowIds, setExpandedRowIds] = useState<GridRowId[]>([]);
    // Pagination
    const [page] = useState(0);
    const [rowsPerPage] = useState(15);
    // Filters & sorting
    const [searchTextUI, setSearchTextUI] = useState("");

    const [searchText, setSearchText] = useState("");
    const [sortField,] = useState<any>("DateSetup");
    const [sortOrder,] = useState<any>("desc");
    const [loading, setLoading] = useState(true);
  
    const [dialogProps, setDialogProps] = useState({
        confirmLabel: "Delete",
        cancelLabel: "Cancel",
        confirmColor: "error" as
            | "inherit"
            | "primary"
            | "secondary"
            | "success"
            | "error"
            | "info"
            | "warning",
        showConfirm: true,
        isProcessing: false,
        processingContent:""
    });

    const navigate = useNavigate();
    const lstDataRef = React.useRef<any[]>([]);
    // Replace both expandedRowIds/detailPanelExpandedRowIds with just one state
   
    // Allow only one row expanded at a time
    const handleRowExpandChange = (newIds: GridRowId[]) => {
        if (newIds.length > 1) {
            // Keep only the last expanded
            const lastId = newIds[newIds.length - 1];
            setExpandedRowIds([lastId]);
        } else {
            setExpandedRowIds(newIds);
        }
    };

    // Master API   
    const { data, isFetching, isSuccess, refetch } = useGetPerseusPaySplitConfigurationsQuery(
        {
            page: page + 1,
            pageSize: rowsPerPage,
            sortField,
            sortDirection: sortOrder,
            searchText,
        },
        { refetchOnMountOrArgChange: true }
    );
    // Lazy load details
    const [triggerDetails, { isFetching: isFetchingDetail }] = useLazyGetSplitConfigDetailsQuery();
    //Delete
    const [deleteSplitCofig] = useDeleteSplitConfigProfileMutation();

    // Update master rows when API returns
    useEffect(() => {
        if (data) {
            const items = data || [];
            lstDataRef.current = items; // assign to ref

            if (items.length === 0 && !isFetching) {
                toast.warning("No Perseus Pay Split Configurations available");
            }
            setMasterRows(
                items.map((item: any, index: number) => ({
                    id: item.id,
                    ...item,
                }))
            );
        }
    }, [data, isFetching]);

    // Watch RTK flags and update custom loader
    useEffect(() => {
        if (!isFetching && isSuccess) {
            setLoading(false);
        }
    }, [isFetching, isSuccess]);

    // Track expanded rows and load detail data when expanded
    useEffect(() => {
        expandedRowIds.forEach((id) => {
            const masterRow = masterRows.find((r) => r.id === id);
            if (masterRow) {
                loadDetailData(masterRow);
            }
        });
    }, [expandedRowIds, masterRows]);

    // Load detail data for a master row
    const loadDetailData = async (master: any) => {
        const key = master.id; // for detailData lookup
        if (!detailData[key]) {
            try {
                const result = await triggerDetails({
                    splitConfigurationId: master.splitConfigurationId,
                    pricingModelId: master.id,
                }).unwrap();

                const detailsWithIds = result.map((item: any, index: number) => ({
                    id: index + 1,
                    ...item,
                }));

                setDetailData((prev) => ({ ...prev, [key]: detailsWithIds }));
            } catch (error) {
                toast.error("Failed to load detail data");
            }
        }
    };

   
    
    // Columns
    const masterColumns: GridColDef[] = [
        /* { field: "splitConfigurationId", headerName: "Split Config Id", width: 220 },*/
        /*{ field: "storeCount", headerName: "Dealers", width: 80, align: "right" },        */
        { field: "name", headerName: "Name", flex: 1, pinnable: false,  hideable:false },
        /*{ field: "description", headerName: "Description", width: 200 },*/
        { field: "pricingType", headerName: "Pricing Type", flex: 1, pinnable: false, hideable: false },
        { 
            field: "isSimple", 
            headerName: "Pricing Mode", 
            flex: 1, 
            pinnable: false, 
            hideable: false, 
            renderCell: (params) => (
                <Typography variant="body2">
                    {params.value ? "Simple" : "Enhanced"}
                </Typography>
            ),
        },
        { field: "merchantName", headerName: "Merchant", flex: 1, pinnable: false, hideable: false },
        { field: "currency", headerName: "Currency", width: 100, filterable: false, disableColumnMenu: true, pinnable: false },
        {
            field: "dealersCount",
            headerName: "Dealers",
            width: 100,
            pinnable: false,
            align: "right",
            renderCell: (params) => {
                if (params.value > 0) {
                    return (
                        <Stack direction="row" spacing={1}>
                            <Link to="#"> {params.value} </Link>
                        </Stack>
                    );
                }

                return (
                    <Typography variant="body2" color="textSecondary">
                        {params.value}
                    </Typography>
                );
            },
        },
        { field: "markupRate", headerName: "Default Markup Rate (%)", flex: 1, headerAlign: "right", align: "right", pinnable: false },
        { field: "markupFee", headerName: "Default Markup Fee", flex: 1, headerAlign: "right", align: "right", pinnable: false },
        { field: "monthlyFee", headerName: "Default Monthly Fee", flex: 1, headerAlign: "right", align: "right", pinnable: false },
        {
            field: "actions",
            headerName: "Actions",
            disableColumnMenu: true,
            sortable: false,
            hideable: false,
            width: 170,
            renderCell: (params) => {
                return (
                    <Stack direction="row" spacing={1}>
                        <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            onClick={() => handleEditMasterRow(params.row)}
                        >
                            Edit
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => handleDeleteMasterRow(params.row)}
                        >
                            Delete
                        </Button>
                    </Stack>
                );
            },
        },
    ];
    
    const detailColumns: GridColDef[] = [
        { field: "cardType", headerName: "Funding Source", flex: 1  },
        /*{ field: "cardBrand", headerName: "Brand", flex: 1,   },*/
        {
            field: "cardBrand",
            headerName: "Payment Method",
            flex: 1,
            renderCell: (params:any) => (
                <Stack direction="row" spacing={1} alignItems="center">
                    {/* Image box */}
                    <Box><PaymentMehtodType type={params.row.paymentMethod}></PaymentMehtodType></Box>
                    {/* Text box */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            fontSize: "0.9rem",
                            fontWeight: 500,
                            color: "#333",
                        }}
                    >
                        {params.value}
                    </Box>
                </Stack>
            ),
        },
        { field: "shopperInteraction", headerName: "Shopper Interaction", flex: 1, },
        { field: "markupRate", headerName: "Markup Rate (%)", flex: 1, headerAlign: "right", align: "right" },
        { field: "markupFee", headerName: "Markup Fee", flex: 1, headerAlign: "right", align: "right" },
    ];

    // Handlers
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTextUI(e.target.value);
        if (isEmpty(e.target.value)) {
            setSearchText(e.target.value);
        }
        else if (searchTextUI.length > 2)
            setSearchText(searchTextUI);
        
    };

    const handleDetailUpdate =
        (parentId: number) =>
            (newRow: GridRowModel): GridRowModel => {
                setDetailData((prev) => ({
                    ...prev,
                    [parentId]: prev[parentId].map((row) =>
                        row.id === newRow.id ? { ...newRow } : row
                    ),
                }));
                return newRow;
            };

    const handleDeleteMasterRow = (row: any) => {
        const rowId = row.id;

        if (row?.dealersCount > 0) {
            // Info-only dialog (no delete button)
            setDialogTitle("Cannot Delete");
            setDialogContent(`Pricing model is used by ${row.dealersCount} dealer(s). Cannot delete.`);
            setOnConfirm(() => () => {
                setDialogOpen(false);
            });
            setDialogProps({
                confirmLabel: "OK",
                cancelLabel: "Cancel",
                confirmColor: "primary",
                showConfirm: true,
                isProcessing: false,
                processingContent:""
                
            });
        } else {
            // Deletable row
            setDialogTitle("Delete Pricing Model");
            setDialogContent(`Are you sure you want to delete pricing model ${row.name}?`);
            setOnConfirm(() => async () => {
                setDialogProps({
                    confirmLabel: "OK",
                    cancelLabel: "Cancel",
                    confirmColor: "error",
                    showConfirm: false,
                    isProcessing: true,
                    processingContent: "Deleting please wait ....."
                });
               
                try {
                    const response = await  deleteSplitCofig({
                        splitConfigurationId: row.splitConfigurationId,
                        pricingModelId: row.id,
                    }).unwrap();
                    toast.success(response);
                    await refetch();
                }
                catch (err) {
                    toast.error(err.data.message);
                }
                setDialogOpen(false);
               
            });
            setDialogProps({
                confirmLabel: "Yes",
                cancelLabel: "No",
                confirmColor: "error",
                showConfirm: true,
                isProcessing: false,
                processingContent: ""
            });
        }
        setDialogOpen(true);
    };

    const handleEditMasterRow = (row: any) => {
        const splitConfigurationId = row.splitConfigurationId;
        const id = row.id;
       
        navigate(`../pricingmodelsetup/${id}/${splitConfigurationId}`);  // ✅ navigate with rowId
    };
 
   
    const getDetailPanelContent = ({ row }) => (
        <Box sx={{ width: "100%" }}>
        <Box sx={{ p: 1, pl: 6,width: "85%" }}>       
            <Box sx={{ height: 250, width: "100%" }}>
                <DataGridPro
                        rows={detailData[row.id] || []}
                        
                    columns={[
                        ...detailColumns                        
                    ]}
                        disableSelectionOnClick
                        disableColumnMenu   // hides the 3-dot column menu
                        disableColumnFilter // prevents filtering globally                        
                        hideFooter
                        autoHeight
                        processRowUpdate={handleDetailUpdate(row.id)}
                        sx={{
                            border: "1px solid #ddd",
                            borderRadius: 2,                            
                            "& .MuiDataGrid-columnHeaders": {
                                backgroundColor: "#f0f4f8"
                            },
                            "& .MuiDataGrid-columnHeaderTitle": {
                                fontWeight: "nomal !important", // override MUI default
                                color: "#202020",
                                fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                                fontSize: 14,
                            },
                            "& .MuiDataGrid-cell": {
                                fontSize: "0.9rem",
                                padding: "6px 8px",
                            },
                            "& .MuiDataGrid-row:hover": {
                                backgroundColor: "#f9f9f9",
                            },
                        }}
                        loading={isFetchingDetail}
                />
            </Box>
            </Box>
            <Divider />
        </Box>
    );
    return (
        <>
            <Helmet>
                <title>Pricing Models | {theme.browserTitle}</title>
            </Helmet>
           
            <Box>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                >
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                        Pricing Models
                    </Typography>
                    <Stack direction="row" alignItems="center">
                        {/*<TextField*/}
                        {/*    id="outlined-search"*/}
                        {/*    placeholder="Search"*/}
                        {/*    sx={{ width: "300px" }}*/}
                        {/*    size="small"*/}
                        {/*    value={searchTextUI}*/}
                        {/*    onChange={handleSearchChange}*/}
                        {/*    InputProps={{*/}
                        {/*        startAdornment: (*/}
                        {/*            <InputAdornment position="start">*/}
                        {/*                <SearchIcon />*/}
                        {/*            </InputAdornment>*/}
                        {/*        ),*/}
                        {/*    }}*/}
                        {/*/>*/}

                        <Box sx={{ ml: 2 }}>
                            <Link to="../pricingmodelsetup">
                                <Button variant="contained" color="primary">
                                    Create New Pricing Model
                                </Button>
                            </Link>
                        </Box>
                    </Stack>
                </Stack>
                <Box sx={{
                    height: expandedRowIds.length > 0 ? "80vh" : "80vh",
                    width: "100%",
                }}>
                    <DataGridPro
                        rows={masterRows}
                        columns={masterColumns}
                        pagination
                        paginationMode={"client"}
                        pageSize={15}
                        loading={isFetching}    
                        getDetailPanelContent={getDetailPanelContent}
                        getDetailPanelHeight={() => 'auto'}
                        detailPanelExpandedRowIds={expandedRowIds}   // controlled state
                        onDetailPanelExpandedRowIdsChange={handleRowExpandChange}
                        rowsPerPageOptions={[15]}
                        isRowSelectable={() => false}
                        components={{
                            NoRowsOverlay: CustomNoRowsOverlay,
                            NoResultsOverlay: CustomNoRowsOverlay
                        }}     
                        sx={{
                            border: "1px solid #ddd",
                            "& .MuiDataGrid-columnHeaders": {
                                backgroundColor: "#F9F9F9",
                                color: "#202020",
                                fontFamily: "Roboto, Helvetica, Arial, sans- serif",
                                fontWeight:"bold !important",
                                fontSize: 14
                            },
                            "& .MuiDataGrid-columnHeaderTitle": {
                                fontWeight: "bold !important", // override MUI default
                                color: "#202020",
                                fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                                fontSize: 14,
                            },
                            "& .MuiDataGrid-cell": {
                                fontSize: "0.9rem",
                                padding: "6px 8px",
                            },
                            "& .MuiDataGrid-row:hover": {
                                backgroundColor: "#f9f9f9",
                            },
                        }}
                    />
                </Box>
                <ConfirmDialog
                    open={dialogOpen}
                    title={dialogTitle}
                    content={dialogContent}
                    onClose={() => setDialogOpen(false)}
                    onConfirm={onConfirm}
                    confirmLabel={dialogProps.confirmLabel}
                    confirmColor={dialogProps.confirmColor}
                    showConfirm={dialogProps.showConfirm}
                    cancelLabel={dialogProps.cancelLabel}
                    isProcessing={dialogProps.isProcessing}
                    processingContent={dialogProps.processingContent}
                />
            </Box>
        </>
    );
};

export default PricingModelList;

function CustomNoRowsOverlay() {
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "#666",
            }}
        >
            <img
                src="/images/filter.png" 
                alt="No pricing model found."
                style={{ width: 150, opacity: 0.8, marginBottom: 10 }}
            />
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                No pricing model found.
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 200 }}>
                Adjust your filters and try again.
            </Typography>
        </Box>
    );
}
